import React from 'react';
import {ProductLineItem} from '@wix/wixstores-client-common-components/dist/es/src/ProductLineItem/ProductLineItem';
import {PaymentStatus, Item} from '../../../../../types/app.types';
import {LayoutDataHook} from '../Layout/Layout';
import {useControllerProps} from '../../ControllerContext';
import {useTranslation} from '@wix/yoshi-flow-editor';
import {getSubscriptionDurationOld} from './utils';
import {classes} from './ProductLineItemOverride.st.css';
import Download from 'wix-ui-icons-common/on-stage/Download';
import Calendar from 'wix-ui-icons-common/on-stage/Calendar';
import {ADD_TO_CALENDAR_ACTION_ID, MAX_PRODUCT_OPTION_LENGTH} from '../../../constants';
import {useStyles} from '@wix/tpa-settings/react';
import s from './ProductLineItem.scss';
import stylesParams from '../../../stylesParams';
import {PaymentOptionType} from '@wix/wixstores-graphql-schema-node';
import {ProductLineItemBase} from '../ProductLineItem/ProductLineItemBase';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const CommonProductLineItem = ({
  item,
  withDivider,
}: {
  item: Item;
  withDivider: boolean;
  /* eslint-disable-next-line sonarjs/cognitive-complexity */
}) => {
  const {t} = useTranslation();
  const styleSettings = useStyles();
  const shouldRenderImage = styleSettings.get(stylesParams.THANK_YOU_PAGE_IMAGE_SELECTION);

  const toTitleValueOption = (o: Item['options'][0]) => ({title: o.title, value: o.description});
  const options = item.options
    ? item.options.filter((o) => !o.isFreeText || !!o.description.trim()).map(toTitleValueOption)
    : [];

  const {
    isSubscription,
    subscriptionName,
    subscriptionDuration,
    subscriptionFrequency,
    paymentStatus,
    reportClickOnLinkOnLineItem,
    useProductLineItemFromTYP,
    subscriptionInterval,
    subscriptionInfo,
    subscriptionBillingCycles,
  } = useControllerProps().thankYouPageStore;

  const subscriptionInfoOld = isSubscription
    ? {
        name: subscriptionName,
        duration: getSubscriptionDurationOld({
          t,
          subscriptionDuration,
          subscriptionFrequency,
        }),
      }
    : undefined;

  const isPaidFor =
    (item.paymentOption === PaymentOptionType.FULL_PAYMENT_ONLINE && paymentStatus === PaymentStatus.PartiallyPaid) ||
    paymentStatus === PaymentStatus.Paid;
  const showDigitalProductDownloadLink = item.digitalFileLink && isPaidFor;
  const shouldHidePrice = item.renderingConfig?.hidePrice;
  const shouldHideQuantity = item.renderingConfig?.hideQuantity;
  const shouldShowPaymentTypeLabel = item.paymentOption && item.paymentOption !== PaymentOptionType.FULL_PAYMENT_ONLINE;

  const handleClickOnLineItem = () => {
    reportClickOnLinkOnLineItem(item);
  };
  const getPaymentTypeLabel = () => {
    const membershipName = item.selectedMembership?.name?.original;
    switch (item.paymentOption) {
      case PaymentOptionType.DEPOSIT_ONLINE:
        return t('thankyouPage.myCartPaymentMethod.deposit.lineItem', {price: item.formattedDepositAmount});
      case PaymentOptionType.MEMBERSHIP:
        return membershipName
          ? t('thankyouPage.myCartPaymentMethod.onlinePlan.lineItem', {membership: membershipName})
          : t('thankyouPage.myCartPaymentMethod.payWithPlan.lineItem');
      case PaymentOptionType.MEMBERSHIP_OFFLINE:
        return membershipName
          ? t('thankyouPage.myCartPaymentMethod.offlinePlan.lineItemWithId', {membership: membershipName})
          : t('thankyouPage.myCartPaymentMethod.offlinePlan.lineItemWithoutId');
      case PaymentOptionType.FULL_PAYMENT_OFFLINE:
        return item.price > 0 && t('thankyouPage.myCartPaymentMethod.offlinePayment.lineItem');
      /* istanbul ignore next */
      default:
        return '';
    }
  };

  const action = getProductLineAction(item, handleClickOnLineItem, useProductLineItemFromTYP);
  const paymentTypeLabel = getPaymentTypeLabel();
  const lineItemPrice = item.lineItemPrice?.formattedAmount;

  return useProductLineItemFromTYP ? (
    <div data-hook={LayoutDataHook.productLineItem} className={s.productLineItem} key={item.productId}>
      <ProductLineItemBase withDivider={withDivider}>
        {shouldRenderImage && (
          <ProductLineItemBase.Image
            product={{name: item.name, media: [item.media?.url ? item.media : null], productType: item.productType}}
          />
        )}
        <ProductLineItemBase.Name name={item.name} className={classes.myName} />
        {!shouldHidePrice && (
          <ProductLineItemBase.Price formattedPrice={item.formattedPrice} className={classes.myPrice} />
        )}
        {!shouldHidePrice && (
          <ProductLineItemBase.TotalPrice
            formattedTotalPrice={lineItemPrice}
            className={classes.myTotalPrice}
            frequency={subscriptionFrequency}
            interval={subscriptionInterval}
            billingCycles={subscriptionBillingCycles}
          />
        )}
        {shouldShowPaymentTypeLabel && <ProductLineItemBase.PaymentTypeLabel label={paymentTypeLabel} />}
        <ProductLineItemBase.Options
          itemId={Number(item.productId)}
          options={options}
          className={classes.myOptions}
          maxOptionValLength={MAX_PRODUCT_OPTION_LENGTH}
        />
        {!shouldHideQuantity && (
          <ProductLineItemBase.ReadOnlyQuantity
            message={t('thankYouPage.quantity.label')}
            quantity={item.quantity}
            className={classes.myReadOnlyQuantity}
          />
        )}
        {(subscriptionInfo || subscriptionInfoOld) && (
          <ProductLineItemBase.Subscription
            itemId={Number(item.productId)}
            subscriptionInfo={subscriptionInfo}
            subscription={subscriptionInfoOld}
            className={classes.mySubscription}
          />
        )}
        {showDigitalProductDownloadLink && (
          <ProductLineItemBase.Link
            title={t('thankYou.DOWNLOAD_LINK_LABEL')}
            icon={<Download className={classes.linkIcon} />}
            href={item.digitalFileLink}
            onClick={handleClickOnLineItem}
            className={classes.myLink}
          />
        )}
        {action}
      </ProductLineItemBase>
    </div>
  ) : (
    <div data-hook={LayoutDataHook.productLineItem} className={s.productLineItem} key={item.productId}>
      <ProductLineItem withDivider={withDivider}>
        {shouldRenderImage && (
          <ProductLineItem.Image
            product={{name: item.name, media: [item.media?.url ? item.media : null], productType: item.productType}}
          />
        )}
        <ProductLineItem.Name name={item.name} className={classes.myNameOld} />
        {!shouldHidePrice && (
          <ProductLineItem.Price formattedPrice={item.formattedPrice} className={classes.myPriceOld} />
        )}
        {!shouldHidePrice && (
          <ProductLineItem.TotalPrice formattedTotalPrice={lineItemPrice} className={classes.myTotalPriceOld} />
        )}
        {shouldShowPaymentTypeLabel && <ProductLineItem.PaymentTypeLabel label={paymentTypeLabel} />}
        <ProductLineItem.Options
          itemId={Number(item.productId)}
          options={options}
          className={classes.myOptionsOld}
          maxOptionValLength={MAX_PRODUCT_OPTION_LENGTH}
        />
        {!shouldHideQuantity && (
          <ProductLineItem.ReadOnlyQuantity
            message={t('thankYouPage.quantity.label')}
            quantity={item.quantity}
            className={classes.myReadOnlyQuantityOld}
          />
        )}
        {!!subscriptionInfoOld && (
          <ProductLineItem.Subscription
            itemId={Number(item.productId)}
            subscription={subscriptionInfoOld}
            className={classes.mySubscriptionOld}
          />
        )}
        {showDigitalProductDownloadLink && (
          <ProductLineItem.Link
            title={t('thankYou.DOWNLOAD_LINK_LABEL')}
            icon={<Download className={classes.linkIconOld} />}
            href={item.digitalFileLink}
            onClick={handleClickOnLineItem}
            className={classes.myLinkOld}
          />
        )}
        {action}
      </ProductLineItem>
    </div>
  );
};

function getProductLineAction(item: Item, handleClickOnLineItem: () => void, useProductLineItemFromTYP: boolean) {
  if (!item.actions?.length) {
    return null;
  }

  const action = item.actions[0];
  return useProductLineItemFromTYP ? (
    <ProductLineItemBase.Link
      key={action.actionId}
      title={action.label}
      icon={
        action.actionId === ADD_TO_CALENDAR_ACTION_ID ? (
          <Calendar data-hook={LayoutDataHook.calendarIcon} className={classes.linkIcon} />
        ) : null
      }
      href={action.url}
      onClick={handleClickOnLineItem}
      className={classes.myLink}
    />
  ) : (
    <ProductLineItem.Link
      key={action.actionId}
      title={action.label}
      icon={
        action.actionId === ADD_TO_CALENDAR_ACTION_ID ? (
          <Calendar data-hook={LayoutDataHook.calendarIcon} className={classes.linkIcon} />
        ) : null
      }
      href={action.url}
      onClick={handleClickOnLineItem}
      className={classes.myLinkOld}
    />
  );
}
